import { Big } from 'big.js';
import {
    AbstractDeviceConsumablePricingItem,
    Article,
    ConsumablePricingItem,
    DeviceConsumableWithSuppPricingItem,
    DevicePricingItem,
    EnumCorridorColor,
    EnumQuoteServiceSalesType,
    EnumShape,
    MathUtils,
    PI,
    PricingItem,
    bigToString,
    isBlank,
    parseBig
} from 'quote-domain-core';
import { ArticleService } from '../article/article-service';
import { CorridorService } from '../corridor/corridor-service';
import { singleton } from 'tsyringe';

@singleton()
export class PricingItemCommercialService {
    constructor(
        private readonly articleService: ArticleService,
        private readonly corridorService: CorridorService
    ) {
    }

    public calculateExtraPrice(pricingItem: PricingItem, referentialServices: any, referential: any): void {
        if (!(pricingItem instanceof ConsumablePricingItem || pricingItem instanceof AbstractDeviceConsumablePricingItem)) {
            return;
        }
        let serviceParameters = null;
        if (referentialServices) {
            serviceParameters = referentialServices['parameters'];
        }
        if (serviceParameters && !pricingItem.isCmc) {
            const coefFs = this.getCoeffFsByFrequency(pricingItem, referential);
            const result = this.articleService.calculateExtraPrices(pricingItem.consumable, serviceParameters, coefFs, pricingItem.consumableExtraPrice, pricingItem.oldConsumableExtraPrice);
            Object.assign(pricingItem, { consumableExtraPrice: result.consumableExtraPrice, oldConsumableExtraPrice: result.oldConsumableExtraPrice });
        }
    }

    public calculateTheoreticalEffectif(pricingItem: PricingItem, referential: any, gender: string): void {
        if (!(pricingItem instanceof AbstractDeviceConsumablePricingItem)) {
            return;
        }
        const ratio = this.getRatio(pricingItem, gender, referential);
        if (!MathUtils.bigIsNullOrZero(ratio)) {
            const consoQty = pricingItem.userInputs['consumableQuantity'];
            const effTheo = parseBig(consoQty).div(ratio);
            Object.assign(pricingItem, { effTheo: Math.floor(Number(effTheo)) });
        }
    }

    private getRatio(pricingItem: AbstractDeviceConsumablePricingItem, gender: string, referential: any): Big {
        const frequency = pricingItem.userInputs && pricingItem.userInputs.frequency?.key;
        const ratioMenOrWomen = pricingItem.specificities && pricingItem.specificities[gender];
        if (ratioMenOrWomen && frequency) {
            const deliveryWeeks = referential.frequencies[referential.frequencies.findIndex((element: any) => element.frequency === frequency)].deliveryWeeks;
            return parseBig(ratioMenOrWomen).div(new Big('4')).mul(new Big(deliveryWeeks));
        }
        return null;
    }

    public calculateCollectorPrice(pricingItem: PricingItem, referentialServices: any, referential: any): void {
        if (!(pricingItem instanceof DevicePricingItem)) {
            return;
        }
        let serviceParameters = null;
        if (referentialServices) {
            serviceParameters = referentialServices['parameters'];
        }
        if (serviceParameters && !pricingItem.isCmc) {
            const coefFs = this.getCoeffFsByFrequency(pricingItem, referential);
            const result = this.articleService.calculateCollectorPrice(pricingItem.device, serviceParameters, coefFs, pricingItem.collectorExtraPrice, pricingItem.oldCollectorExtraPrice);
            if (result) {
                Object.assign(pricingItem, { collectorExtraPrice: result.collectorExtraPrice, oldCollectorExtraPrice: result.oldCollectorExtraPrice });
            }
        }
    }

    public calculateAdditionalConsumablePrice(pricingItem: PricingItem, contractInfoInputs: any, code: string, nonNegociableArticleColor: string, referentialServices: any, referential: any): void {
        if (!(pricingItem instanceof DeviceConsumableWithSuppPricingItem)) {
            return;
        }
        const device = pricingItem.device;
        const consumable = pricingItem.consumable;
        if (!this.isQteGteZero(pricingItem.userInputs.consumableQuantity) && this.isQteGteZero(pricingItem.userInputs.quantity)) {
            const tmpAdditionalConsumable = new Article();
            tmpAdditionalConsumable.productPricings = consumable.productPricings;
            tmpAdditionalConsumable.distance = null;
            tmpAdditionalConsumable.appliedPriceColor = device.appliedPriceColor;
            tmpAdditionalConsumable.oldDistance = device.distance;
            tmpAdditionalConsumable.oldAppliedPriceColor = device.appliedPriceColor;
            tmpAdditionalConsumable.oldAppliedPrice = null;
            tmpAdditionalConsumable.appliedPrice = null;
            const userInputsForCorridorSearch: any = {};
            Object.assign(userInputsForCorridorSearch, pricingItem.userInputs, contractInfoInputs);
            userInputsForCorridorSearch['quantity'] = pricingItem.userInputs.consumableQuantity > 0 ? consumable.totalQuantity : 1;
            this.articleService.updateCorridor(tmpAdditionalConsumable, userInputsForCorridorSearch, code, pricingItem.isCmc, EnumQuoteServiceSalesType.NEW);
            this.articleService.calculateArticleDistanceAndColorAndPriceFixedCorridorMode(tmpAdditionalConsumable, nonNegociableArticleColor);
            if (!pricingItem.isCmc) {
                const serviceParameters = referentialServices['parameters'];
                const coefFs = this.getCoeffFsByFrequency(pricingItem, referential);
                const extraPriceCoefficient = serviceParameters?.extraPriceCoefficient;
                const extraPrice = parseBig(tmpAdditionalConsumable.appliedPrice).mul(coefFs).mul(parseBig('1').add(parseBig(extraPriceCoefficient)));
                Object.assign(pricingItem, { consumableExtraPrice: extraPrice ? extraPrice.toFixed(40) : pricingItem.consumableExtraPrice });
            }
            Object.assign(pricingItem, { additionalConsumablePrice: tmpAdditionalConsumable.appliedPrice });
        } else {
            if (consumable.corridor) {
                let matchedColor = null;
                if (this.articleService.isFlat(consumable)) {
                    matchedColor = nonNegociableArticleColor;
                } else {
                    matchedColor = device.appliedPriceColor === EnumCorridorColor.GRAY || device.appliedPriceColor == null || device.appliedPriceColor == undefined ?
                        consumable.appliedPriceColor ?? EnumCorridorColor.GREEN : device.appliedPriceColor;
                }
                const matchedLevelByColor = this.corridorService.getMatchedLevelByColor(consumable.corridor, matchedColor);
                const newAppliedPriceWithDistance = this.articleService.getNewAppliedPriceWithDistance(matchedLevelByColor, device.distance ? device.distance : consumable.distance);
                consumable.discount = device.appliedPrice ? device.discount : consumable.discount;
                consumable.appliedPriceColor = device.appliedPrice ? device.appliedPriceColor : consumable.appliedPriceColor;
                consumable.appliedPrice = bigToString(newAppliedPriceWithDistance);
            }
            Object.assign(pricingItem, { additionalConsumablePrice: consumable.appliedPrice, discount: device.discount, totalAppliedPriceColor: device.appliedPriceColor });
        }
    }

    private isQteGteZero(quantity: number): boolean {
        return quantity > 0;
    }

    public calculateRecommendedConsumableNumber(pricingItem: PricingItem, referential: any, numberOfDaysOpen: Big, menEffectif: any, womenEffectif: any, visitorEffectif: any, menAndWomenEffectif: any): void {
        if (!(pricingItem instanceof AbstractDeviceConsumablePricingItem)) {
            return;
        }
        const specificities = pricingItem.specificities;
        const ratioM = specificities && specificities.ratioM;
        const ratioW = specificities && specificities.ratioW;
        const ratioMenAndWomen = specificities && specificities['ratioM/W'];
        let recommendedConsumableNumber = null;
        if (!isBlank(pricingItem.userInputs.frequency)) {
            const weeks = this.findWeeksNumberFromFrequency(referential, pricingItem.userInputs.frequency.value);
            const effectiveV = parseBig(pricingItem.userInputs.effectivesV && pricingItem.userInputs.effectivesV !== '0' ? pricingItem.userInputs.effectivesV : visitorEffectif);
            if (ratioMenAndWomen) {
                const appliedFrequencyRatioMenAndWomen = this.getAppliedFrequencyRatio(ratioMenAndWomen, weeks);
                const effectiveMenAndWomen = parseBig(pricingItem.userInputs.menAndWomenEffectif && pricingItem.userInputs.menAndWomenEffectif !== '0' ? pricingItem.userInputs.menAndWomenEffectif : menAndWomenEffectif);
                recommendedConsumableNumber = this.getRecommendedConsumableNumber(appliedFrequencyRatioMenAndWomen, numberOfDaysOpen, effectiveMenAndWomen, effectiveV);
            } else if (ratioM && ratioW) {
                const appliedFrequencyRatioM = this.getAppliedFrequencyRatio(ratioM, weeks);
                const appliedFrequencyRatioW = this.getAppliedFrequencyRatio(ratioW, weeks);
                const effectiveM = parseBig(pricingItem.userInputs.effectivesM && pricingItem.userInputs.effectivesM !== '0' ? pricingItem.userInputs.effectivesM : menEffectif);
                const effectiveW = parseBig(pricingItem.userInputs.effectivesW && pricingItem.userInputs.effectivesW !== '0' ? pricingItem.userInputs.effectivesW : womenEffectif);
                const recommendedConsumableNumberH = this.getRecommendedConsumableNumber(appliedFrequencyRatioM, numberOfDaysOpen, effectiveM, effectiveV);
                const recommendedConsumableNumberF = this.getRecommendedConsumableNumber(appliedFrequencyRatioW, numberOfDaysOpen, effectiveW, effectiveV);
                recommendedConsumableNumber = recommendedConsumableNumberH.plus(recommendedConsumableNumberF);
            }
            if (recommendedConsumableNumber != null) {
                Object.assign(pricingItem, { userInputs: { ...pricingItem.userInputs, recommendedConsumableNumber: (MathUtils.round(recommendedConsumableNumber.round(0, 3), 0)) + '' } });
            }
        }
    }

    private getAppliedFrequencyRatio(ratio: string, weeks: string): Big {
        return (new Big(ratio).div(new Big('4'))).mul(new Big(weeks));
    }

    private findWeeksNumberFromFrequency(referential: any, frequency: string): string {
        return referential.frequencies[referential.frequencies.findIndex((element: any) => element.frequency === frequency)].deliveryWeeks;
    }

    private getRecommendedConsumableNumber(appliedFrequencyRatio: any, numberOfDaysOpen: any, effective: any, effectiveV: any): Big {
        return (((appliedFrequencyRatio.div(new Big('5'))).mul(numberOfDaysOpen)).mul(effective.plus(effectiveV.mul(new Big('0.2')).mul(new Big('0.5')))));
    }

    public applyServiceStockSuppOffered(pricingItem: PricingItem, contractDuration: string): void {
        if (!(pricingItem instanceof AbstractDeviceConsumablePricingItem)) {
            return;
        }
        if (!pricingItem.isCmc) {
            const impactSuppOffer = this.calculateImpactSuppOffer(pricingItem, contractDuration);
            this.articleService.updateLastCorridorStateByMultiplyingImpactSuppOffer(pricingItem.consumable, impactSuppOffer);
        }
    }

    private calculateImpactSuppOffer(pricingItem: AbstractDeviceConsumablePricingItem, contractDuration: string): number {
        const franchiseConso = parseInt(pricingItem.franchise);
        const deviceQty = parseInt(pricingItem.userInputs.quantity);
        const consumableQty = parseInt(pricingItem.userInputs.consumableQuantity);
        const durationContract = parseInt(contractDuration);
        const stockSuppCount = Math.max(franchiseConso * deviceQty - consumableQty, 0);
        return stockSuppCount / (durationContract * consumableQty);
    }

    public applyCorridorCustomCarpet(pricingItem: PricingItem): void {
        if (!(pricingItem instanceof DevicePricingItem)) {
            return;
        }
        if (this.articleService.isCustomCarpet(pricingItem.device) && pricingItem.device.lastCorridorState) {
            const shapeSurface = this.getShapeSurface(pricingItem.userInputs);
            if (shapeSurface.gt(new Big(0))) {
                this.articleService.updateLastCorridorStateBymultiplyingSahpeSurfec(pricingItem.device, shapeSurface);
            }
        }
    }

    private getShapeSurface(userInputs: Record<string, any>): Big {
        const shape: EnumShape = userInputs?.shape;
        let shapeSurface: Big;
        switch (shape) {
            case EnumShape.RECTANGLE: {
                const width = userInputs.width;
                const height = userInputs.height;
                shapeSurface = parseBig(width).mul(parseBig(height));
                break;
            }
            case EnumShape.CIRCLE:
            case EnumShape.SEMICIRCLE: {
                const diameter = userInputs.diameter;
                const radius = parseBig(diameter).div(2);
                shapeSurface = new Big(PI).mul(radius.pow(2));
                if (shape === EnumShape.SEMICIRCLE) {
                    shapeSurface = shapeSurface.div(2);
                }
                break;
            }
            default:
                shapeSurface = new Big(1);
                break;
        }
        return shapeSurface;
    }

    private getCoeffFsByFrequency(pricingItem: PricingItem, referential: any): Big {
        const frequency = pricingItem.userInputs.frequency.value;
        const index = referential.frequencies.findIndex((element: any) => element.frequency === frequency);
        if (index >= 0 && !!referential.frequencies[index].coeffFs) {
            return parseBig(referential.frequencies[index].coeffFs?.toString());
        }
        return new Big('1');
    }

    public splitAppliedPrice(pricingItem: PricingItem, nonNegociableArticleColor: string): void {
        if (!(pricingItem instanceof DeviceConsumableWithSuppPricingItem)) {
            return;
        }
        if (pricingItem.deviceConsumableCorridor) {
            const color = this.corridorService.getMatchedColorByappliedPrice(pricingItem.deviceConsumableCorridor, pricingItem.deviceConsumableAppliedPrice, nonNegociableArticleColor);
            const distance = this.corridorService.getDistanceByAppliedPrice(pricingItem.deviceConsumableCorridor, pricingItem.deviceConsumableAppliedPrice);
            const corridorLevelDevice = pricingItem.device.corridor ? this.corridorService.getMatchedLevelByColor(pricingItem.device.corridor, color) : undefined;
            const corridorLevelConsumable = pricingItem.consumable.corridor ? this.corridorService.getMatchedLevelByColor(pricingItem.consumable.corridor, color) : undefined;
            if (distance) {
                this.articleService.applyServiceGlobalDiscountToArticle(pricingItem.device, distance.toString(), corridorLevelDevice, color);
                this.articleService.applyServiceGlobalDiscountToArticle(pricingItem.consumable, distance.toString(), corridorLevelConsumable, color);
            }
        }
    }
}
